import { useEffect, useRef, useState } from "react";

/**
 * AnimatedNumber — counts from 0 (or `from`) up to `value` over `duration` ms
 * using ease-out-cubic. Re-animates only when `value` changes by > 0.01.
 *
 * The actual rendered string goes through `format` so we can pass fmtMoney
 * or any custom formatter.
 *
 * Usage:
 *   <AnimatedNumber value={1234.56} format={fmtMoney} />
 */
export default function AnimatedNumber({
  value,
  format = (v) => v,
  duration = 900,
  from = 0,
  className = "",
}) {
  const [display, setDisplay] = useState(from);
  const rafRef = useRef(null);
  const startRef = useRef(null);
  const fromRef = useRef(from);

  useEffect(() => {
    // Skip animation for non-numeric or null
    if (typeof value !== "number" || !isFinite(value)) {
      setDisplay(value ?? 0);
      return;
    }

    fromRef.current = display;
    startRef.current = null;

    const tick = (ts) => {
      if (startRef.current === null) startRef.current = ts;
      const elapsed = ts - startRef.current;
      const t = Math.min(elapsed / duration, 1);
      // Ease-out cubic
      const eased = 1 - Math.pow(1 - t, 3);
      const next = fromRef.current + (value - fromRef.current) * eased;
      setDisplay(next);
      if (t < 1) {
        rafRef.current = requestAnimationFrame(tick);
      } else {
        setDisplay(value);
      }
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, duration]);

  return <span className={className}>{format(display)}</span>;
}
